<template>
  <div class="products-ctn">
    <h4 class="text-capitalize font-weight-bold title">{{$t('amazon_transparency_products')}}</h4>

    <div class="content mr-4">
      <div class="mt-6" v-if="isATPEnabled">
          <!-- button with search text field    -->
        <div class="d-flex flex-wrap align-center">
          <v-btn
            v-if="hasATPEditPermission"
            class="text-uppercase"
            color="#15BBF0"
            @click="handleConnectProduct"
          >
            {{$t('amazon_transparency_connect_product')}}
          </v-btn>
          <v-spacer></v-spacer>
          <v-text-field
            outlined
            dense
            v-if="hasConnectedProducts || searchProducts || hasLoadedProducts"
            v-model="searchProducts"
            @input="handleSearchInput"
            class="d-flex align-center custom-search-field mt-4 mt-sm-0"
            :placeholder="$t('amazon_transparency_search_by')"
            single-line
            persistent-hint
          >
            <template v-slot:append>
              <v-icon
                color="black"
                @click="handleSearchIconClick"
                class="cursor-pointer"
              >
                {{ searchProducts ? 'mdi-close' : 'mdi-magnify' }}
              </v-icon>
            </template>
          </v-text-field>
        </div>

        <!--  data table -->
        <div class="mt-6" v-if="hasConnectedProducts || searchProducts || hasLoadedProducts">
          <v-data-table
            :headers="headers"
            :items="atpProducts"
            :loading="isLoadingATPProducts"
            :options.sync="options"
            :server-items-length="totalATPProducts"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-text': '',
              'disable-items-per-page': true
            }"
            class="custom-data-table"
            item-value="id"
            :loading-text="$t('amazon_transparency_loading_products')"
            :item-class="(item) => (item.id === updatedATPProductId ? 'highlighted-row' : '')"
            hide-default-footer
          >

            <!-- product name header  -->
            <template v-slot:header.product_name="{ header }">
               <span class="text-no-wrap">{{ header.text }}</span>
            </template>

            <!-- connected codes header -->
            <template v-slot:header.uploaded_code_count="{ header }">
              <div class="d-flex align-center justify-center text-no-wrap">
                <span>{{ header.text }}</span>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="primary"
                      size="18"
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <div class="help-container">{{ $t('amazon_transparency_status_help')}}</div>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:item.sku="{ item }">
                <span class="wrap-cell-text">{{ item.sku }}</span>
            </template>

            <!-- connection status row item -->
            <template v-slot:item.connection_status="{ item }">
              <span class="text-no-wrap" :class="[ item.status !== 'in progress' ? 'text-capitalize' : '', getStatusClass(item.status)]">
                {{ item.status }}
              </span>
            </template>

            <!-- auto upload row item -->
            <template v-slot:item.is_active="{ item }">
              <span class="text-capitalize" :class="getStatusClass(item.status)">
                <v-icon v-if="item.is_active" color="green" size="24">mdi-check</v-icon>
                <v-icon v-else color="red">mdi-close</v-icon>
              </span>
            </template>
            <!-- action items -->
            <template v-slot:item.actions="{ item }">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon color="primary">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="viewItemDetails(item)">
                    <v-list-item-title>{{ $t('amazon_transparency_view_details') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="reconnectItem(item)" v-if="item.status !== 'connected' && hasATPEditPermission">
                    <v-list-item-title>{{ $t('amazon_transparency_reconnect') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <!-- table footer  -->
            <template v-slot:footer>
              <div class="d-flex justify-end mt-5 mb-4">
                <v-pagination
                  class="flex-grow-0"
                  v-model="options.page"
                  :length="totalPages"
                  :total-visible="7"
                  @click.stop
                />
              </div>
            </template>
          </v-data-table>
        </div>

        <div class="flex justify-center align-center no-products-text" v-if="hasNoConnectedProducts && !searchProducts && !hasLoadedProducts">
          {{$t('amazon_transparency_no_products')}}
        </div>
      </div>

      <div v-if="!isATPEnabled">
        <div class="mt-10">
          <p class="content-text">{{$t('amazon_transparency_auto_register')}}</p>
          <p class="content-text mt-6">
            <span class="font-weight-bold">{{$t('amazon_transparency_already_register')}}</span>
            {{$t('amazon_transparency_use_module')}}
          </p>
          <p class="mt-6 content-text">
            {{$t('amazon_transparency_please_contact')}}
            <a href="mailto:support@scantrust.com" class="text-decoration-underline">support@scantrust.com</a>
            {{$t('amazon_transparency_enable_feature')}}
          </p>
        </div>
      </div>
    </div>
    <ConnectProductDialog :display-connect-product-dialog="displayDialog" @update:displayConnectProductDialog="closeDialog"/>
  </div>
</template>

<script>
import ConnectProductDialog from '@/components/amazon-transparency/dialog/ConnectProductDialog.vue'
import utils from '@/utils/utils'
import { mapGetters } from 'vuex'

export default {
  name: 'Products',
  components: { ConnectProductDialog },
  data () {
    return {
      displayDialog: false,
      searchProducts: '',
      headers: [
        { text: this.$t('amazon_transparency_product_name'), align: 'start', sortable: false, value: 'product_name', width: '15%' },
        { text: this.$t('amazon_transparency_sku'), value: 'sku', sortable: false, align: 'center', width: '17%' },
        { text: this.$t('amazon_transparency_gtin_14'), value: 'gtin', sortable: false, align: 'center', width: '8%' },
        { text: this.$t('amazon_transparency_tbya_connection_status'), value: 'connection_status', sortable: false, align: 'center', width: '15%', class: 'text-no-wrap' },
        { text: this.$t('amazon_transparency_auto_upload'), value: 'is_active', sortable: false, align: 'center', width: '10%', class: 'text-no-wrap' },
        { text: this.$t('amazon_transparency_connected_codes'), value: 'uploaded_code_count', sortable: false, align: 'center', width: '14%' },
        { text: this.$t('amazon_transparency_created_at'), value: 'created_at', sortable: false, align: 'center', width: '18%', cellClass: 'text-no-wrap' },
        { text: this.$t('amazon_transparency_actions'), value: 'actions', sortable: false, align: 'right', width: '3%' }
      ],
      defaultParams: { ordering: '-created_at', limit: 10 },
      options: {
        page: 1,
        itemsPerPage: 10
      },
      debounceSearch: null
    }
  },
  computed: {
    ...mapGetters(['getATPConfig', 'atpProducts', 'isLoadingATPProducts', 'totalATPProducts', 'hasLoadedProducts', 'updatedATPProductId', 'isAllowed', 'requesterParty']),
    isATPEnabled () {
      return this.getATPConfig && this.getATPConfig.is_enabled
    },
    hasConnectedProducts () {
      return this.atpProducts.length > 0
    },
    hasNoConnectedProducts () {
      return this.atpProducts.length === 0
    },
    totalPages () {
      return Math.ceil(this.totalATPProducts / this.options.itemsPerPage)
    },
    accountId () {
      return this.getATPConfig.account_id
    },
    hasATPEditPermission () {
      return this.isAllowed('amz_transparency_edit')
    },
    isStagingOrDevEnvironment () {
      return utils.isDevEnvironment() || utils.isStagingEnvironment()
    }
  },
  methods: {
    handleConnectProduct () {
      this.displayDialog = true
    },
    closeDialog () {
      this.displayDialog = false
    },
    viewItemDetails (item) {
      this.$router.push({
        name: 'amazon-transparency.product-details',
        params: { id: item.id }
      })
      const payload = { ...item }
      this.$store.dispatch('setSelectedProduct', payload)
    },
    getStatusClass (status) {
      const lowerStatus = status.toLowerCase()
      if (lowerStatus === 'connected') {
        return 'status-connected'
      } else if (lowerStatus === 'processing') {
        return 'status-processing'
      } else if (lowerStatus === 'in progress') {
        return 'status-in-progress'
      } else {
        return 'status-failed'
      }
    },
    async reconnectItem (item) {
      const payload = { productId: item.id }
      await this.$store.dispatch('reconnectATPProduct', payload)
    },
    getUrlParams (extraParams) {
      const merged = { ...this.defaultParams, ...extraParams }
      const params = new URLSearchParams()

      for (const key in merged) {
        if (merged[key] != null && merged[key] !== '') {
          params.append(key, merged[key])
        }
      }

      return params
    },
    async displaySnackbar (message) {
      await this.$store.dispatch('showSnackBar', { message: message })
    },
    async loadProducts () {
      try {
        if (this.isLoadingATPProducts) return
        const { page = 1, itemsPerPage = 10 } = this.options
        const extraParams = {
          offset: (page - 1) * itemsPerPage,
          limit: itemsPerPage
        }

        if (this.searchProducts !== '') {
          extraParams.search = this.searchProducts.trim()
        }

        const payload = this.getUrlParams(extraParams)
        await this.$store.dispatch('setIsLoadingATPProducts', true)
        await this.$store.dispatch('loadATPProducts', payload)
      } catch (error) {
        await this.$store.dispatch('setIsLoadingATPProducts', false)
        if (error?.response) {
          await this.displaySnackbar(this.$t('amazon_transparency_error_occurred'))
        } else if (error?.request) {
          await this.displaySnackbar(this.$t('amazon_transparency_no_response'))
        } else {
          await this.displaySnackbar(this.$t('amazon_transparency_error_occurred'))
        }
        await this.$store.dispatch('setATPProducts', [])
      } finally {
        await this.$store.dispatch('setIsLoadingATPProducts', false)
      }
    },
    handleSearchInput (input) {
      const inputVal = input.trim()
      if (!inputVal) return

      this.debounceSearch()
    },
    async clearSearch () {
      this.searchProducts = ''
      await this.loadProducts()
    },
    handleSearchIconClick () {
      if (this.searchProducts) {
        this.clearSearch()
      }
    }
  },
  watch: {
    searchProducts (newVal) {
      if (newVal === '' && this.atpProducts.length === 0) {
        this.loadProducts()
      }
    },
    options: {
      handler () {
        this.loadProducts()
      },
      deep: true
    },
    updatedATPProductId (newVal) {
      if (newVal) {
        setTimeout(() => {
          this.$store.dispatch('setUpdatedATPProductId', null)
        }, 2000)
      }
    }
  },
  async created () {
    try {
      await this.loadProducts()
      this.debounceSearch = utils.debounce(() => {
        this.$store.dispatch('setATPProducts', [])
        this.loadProducts()
      }, 400, false)
      if (this.isStagingOrDevEnvironment && this.isATPEnabled) {
        await this.$store.dispatch('getRequesterPartyDetail', this.accountId)
      }
    } catch (_) {
      if (this.isStagingOrDevEnvironment) {
        // party expired but account enabled.
        if (!this.requesterParty && this.isATPEnabled) {
          const mockPayload = { gln: this.accountId }
          await this.$store.dispatch('createRequesterParty', mockPayload)
        }
      }
    }
  }
}

</script>

<style lang="sass">

 .products-ctn
   padding-top: 20px
   max-height: 100vh !important
   overflow-y: auto !important
   -ms-overflow-style: none !important
   scrollbar-width: none !important

   &::-webkit-scrollbar
     display: none !important

   .title
     font-size: 24px
     font-style: normal
     font-weight: 700
     line-height: normal

   .content-text
      font-size: 18px
      font-style: normal
      font-weight: 300
      line-height: 26px

   .no-products-text
     color: rgba(0, 0, 0, 0.87)
     text-align: center
     font-size: 14px
     font-style: normal
     font-weight: 400
     line-height: 20px
     letter-spacing: 0.25px
     margin-top: 44px

   .custom-search-field
    max-width: 507px !important

    .v-input__control
      height: 40px !important
      border: 1px solid rgba(0, 0, 0, 0.10) !important
      border-radius: 5px !important
      background: #fff

    .v-input__slot
      height: 40px !important

    input
      height: 40px !important
      line-height: 40px !important

   .custom-data-table
    .v-data-table-header
      background-color: #f5f5f5 !important

    .v-data-table-header th
      font-weight: 700 !important
      color: rgba(0, 0, 0, 0.87) !important
      font-size: 12px !important
      line-height: 20px !important
      letter-spacing: 0.4px !important

    .v-data-footer__select
     visibility: hidden !important
     pointer-events: none !important

    .status-connected
      color: #3FC135
      font-size: 14px
      font-style: normal
      font-weight: 400
      line-height: 20px
      letter-spacing: 0.25px

    .status-failed
      color: #FA4C5B
      font-size: 14px
      font-style: normal
      font-weight: 400
      line-height: 20px
      letter-spacing: 0.25px

    .status-processing
      color: #00AEEF
      font-size: 14px
      font-style: normal
      font-weight: 400
      line-height: 20px
      letter-spacing: 0.25px

    .status-in-progress
      color: #999 !important
      font-size: 14px
      font-style: normal
      font-weight: 400
      line-height: 20px
      letter-spacing: 0.25px

    .no-wrap-cell
      white-space: nowrap !important

</style>
